<template>
    <div class="w-100">
        <div class="d-flex justify-content-center h-50 align-items-center" v-if="loadData">
            <b-spinner variant="info" style="width: 7rem; height: 7rem;"/>
        </div>
        <template v-else>
            <btn-back v-if="editMode || viewMode"/>
            <h5 class="font-weight-bold">
                {{getTitle}} order
            </h5>
            <b-row>
                <address-form :submitted-form="submittedForm"
                              :address="order.address"
                              :disabled="viewMode"
                              ref="addressForm"
                              class="col-xl-9 col-md-9 col-sm-12 needs-validation">
                    <b-row slot="additionalRow" class="align-items-center justify-content-between mb-3">
                        <b-col md="2" v-if="!order.id">
                            <b-btn
                                type="submit"
                                variant="primary-user"
                                :disabled="disabledAddProductButton"
                            >
                                Add product
                            </b-btn>
                        </b-col>
                    </b-row>
                </address-form>
                <b-col md="3" v-if="editMode || viewMode">
                    <h6 class="mb-2 mt-1">Order details</h6>
                    <b-list-group>
                        <b-list-group-item>Origin: <b>{{order.service}}</b></b-list-group-item>
                        <b-list-group-item>Status: <b>{{order.status | orderStatusFormat}}</b></b-list-group-item>
                        <b-list-group-item>Total: <b>{{order.total}} {{order.currencyCode}}</b></b-list-group-item>
                        <b-list-group-item>Parcels: <b>{{order.orderParcels.length}}</b></b-list-group-item>
                        <b-list-group-item>Service name: <b>{{order.delivery.serviceName}}</b></b-list-group-item>
                        <b-list-group-item>Track number: <b>{{order.delivery.trackingNumber}}</b></b-list-group-item>
                    </b-list-group>
                </b-col>
            </b-row>
            <div class="table-user mb-5" v-if="order.id">
                <h5 class="font-weight-bold">Selected products</h5>
                <div class="scroll-x">
                    <table-element
                        :table-busy-disabled="disabledTableBusy"
                        id="selected-products"
                        :key="renderKey"
                        :items="selectedProducts"
                        :row-class="rowClass"
                        :fields="orderProductsFields">
                        <div slot="product.name" slot-scope="data">
                            <div v-if="data.item.product.status !== 'active'" class="d-flex align-items-center">
                                <span v-b-popover.hover="getPopoverText(data.item.product.status)"
                                    class="text-danger product-popover">&#9888;
                                </span>
                                <span>{{data.value}}</span>
                            </div>
                            <template v-else>
                                {{data.value}}
                            </template>
                        </div>
                        <quantity-column slot="orderedQuantity"
                                         slot-scope="data"
                                         :edit="!viewMode"
                                         :item="data.item"
                                         :endpoint="$endpoints.orders.product(order.id, data.item.id)"
                                         @input="changeQuntityOfSelectedProduct"
                                         @changeQuantityValue="changeProductUnits"/>
                        <div slot="action"
                             slot-scope="data" class="d-flex align-items-center">
                            <b-btn v-if="data.item.product.status === 'suspended'"
                                   variant="primary-user"
                                   class="mr-2"
                                   @click="editProduct(data.item.product.id)">
                                Edit
                            </b-btn>
                            <b-btn v-if="data.item.product.status === 'inactive'"
                                   variant="primary-user"
                                   class="mr-2"
                                   @click="openPlanModal(data.item)">
                                Restock
                            </b-btn>
                            <b-btn variant="danger"
                                   class="ml-auto"
                                   v-if="!viewMode"
                                   :disabled="selectedProducts.length === 1"
                                   @click="removeFromOrder(data.item.id, data.item.product.id)">
                                Remove
                            </b-btn>
                        </div>
                    </table-element>
                </div>
                <div class="table-user-footer d-flex">
                    <b-btn v-if="!viewMode"
                           variant="success"
                           class="mr-2"
                           :disabled="$store.state.loading || disabledSendFromProductValidate"
                           @click="validateForm()">Save
                    </b-btn>
                    <b-btn variant="primary-user"
                           v-if="!viewMode && !editMode"
                           :disabled="$store.state.loading || disabledSendFromProductValidate || disabledSendFromAvailable"
                           @click="startFulfillmentFromButton()">{{'Start fulfillment'}}
                    </b-btn>
                    <b-btn v-if="editMode"
                           variant="primary-user"
                           :disabled="$store.state.loading || disabledSendFromProductValidate || disabledSendFromAvailable || disabledProductListButton"
                           @click="startFulfillmentFromButton()">Send order to fulfillment
                    </b-btn>
                    <b-btn v-if="editMode"
                           variant="danger"
                           class="ml-auto"
                           :disabled="$store.state.loading"
                           @click="$root.$emit('bv::show::modal', 'confirm-modal-' + Number(1 + order.id))">
                        Delete order
                    </b-btn>
                    <b-btn v-if="order.status === 'inventory_reserved'"
                           variant="danger"
                           class="ml-auto"
                           :disabled="$store.state.loading"
                           @click="$root.$emit('bv::show::modal', 'confirm-modal-' + Number(2 + order.id))">
                        Cancel order
                    </b-btn>
                </div>
            </div>
            <div class="table-user" v-if="order.id && !viewMode">
                <h5 class="font-weight-bold">Available products</h5>
                <div class="table-user-header mt-2">
                    <b-row class="pb-3">
                        <search-field @searchValue="event => searchField(event)"></search-field>
                    </b-row>
                </div>
                <div class="scroll-x">
                    <table-element
                        :table-busy-disabled="disabledTableBusy"
                        :items="items"
                        :fields="fields"
                        @sort="event => sortTable(event)">
                        <b-btn slot="action"
                               slot-scope="data"
                               variant="primary-user"
                               :disabled="disabledProductListButton"
                               @click="addToOrder(data.item)">
                            Add
                        </b-btn>
                    </table-element>
                </div>
                <div class="table-user-footer">
                    <table-pagination
                        v-if="settingsPaginationFromApi.total >= quantityProductOnPage"
                        @paginateAction="updatePagination"
                        :settings="settingsPaginationFromApi"
                        :perPageArray="quantityProductVariations"/>
                </div>
            </div>
        </template>
        <shipping-plan-modal
            :items="[...shippingPlanProduct]"
            @hide="disabledTableBusy = false"
            @show="disabledTableBusy = true"/>
        <b-modal
            hide-header
            hide-footer
            id="product-edit-modal"
            @hidden="closeModal"
            @hide="disabledTableBusy = false"
            @show="disabledTableBusy = true"
            size="xl">
                <SingleProduct
                    @editProduct="editProductModal"
                    :productId="productId" />
        </b-modal>

        <order-delivery
            @hide="disabledTableBusy = false; $router.go(-1)"
            @show="disabledTableBusy = true"
            @updateList="finishOrder"
            :id="idForDelivery"
            :preferableDelivery="preferableDelivery"
            :orderAddress="orderAddress"
            :minInsurancePrice="minInsurancePrice"
            :showLockers="showLockers" />

        <confirm-modal :id="Number(1 + order.id)" @confirm="deleteOrder"/>
        <confirm-modal :id="Number(2 + order.id)" @confirm="cancelOrder"/>
    </div>
</template>

<script>
    import {tableMixins, paginationMixin} from '@/mixins/tableMixins'

    import TableElement from '~cp/UserPanel/Elements/TableElement'
    import TablePagination from '~cp/UserPanel/Elements/TablePagination'
    import SearchField from '~cp/UserPanel/Elements/SearchField'
    import QuantityColumn from './QuantityColumn'
    import AddressForm from '~cp/UserPanel/Forms/AddressForm'
    import ShippingPlanModal from '~cp/UserPanel/Modals/ShippingPlanModal'

    import orderAddProducts from '@/assets/js/columns/orderAddProducts'
    import orderProducts from '@/assets/js/columns/orderProducts'

    import OrderDelivery from '~cp/UserPanel/Pages/Orders/OrderDelivery'

    import ConfirmModal from '~cp/UserPanel/Modals/ConfirmModal'

    export default {
        name: 'SingleOrder',
        mixins: [tableMixins, paginationMixin],
        components: {
            TableElement,
            SearchField,
            QuantityColumn,
            TablePagination,
            AddressForm,
            ShippingPlanModal,
            OrderDelivery,
            ConfirmModal,
            SingleProduct: () => import('~user-pages/Inventory/SingleProduct')
        },
        data() {
            return {
                disabledTableBusy: false,
                productWasEdit: false,
                fields: orderAddProducts,
                orderProductsFields: orderProducts,
                requiredApiParams: {
                    'filter[status]': 'active',
                    'filter[_onlyAvailable]': true
                },
                selectedProducts: [],
                editMode: false,
                viewMode: false,
                fulfillmentFromButton: false,
                order: {
                    address: null,
                    id: '',
                    currencyCode: 'EUR',
                    fulfillmentOrder: null,
                    orderParcels: [],
                    service: '',
                    status: '',
                    total: 0
                },
                productId: '',
                shippingPlanProduct: {},
                loadData: false,
                renderKey: 1,
                idForDelivery: '',
                preferableDelivery: '',
                minInsurancePrice: null,
                orderAddress: null,
                showLockers: null,
                disabledSendFromAvailable: false,
                disabledProductListButton: false,
                disabledAddProductButton: false
            }
        },
        mounted() {
            if (!!this.$route.params.orderId) {
                this.$set(this.order, 'id', this.$route.params.orderId)
                this.loadData = true
                this.getOrder()
            }
        },
        computed: {
            disabledSendFromProductValidate() {
                if (this.selectedProducts.length > 0) {
                    this.checkDisabledSendFromAvailable()
                    return this.selectedProducts.some(item => {
                        return (
                            item.product.status !== 'active'
                            || item.orderedQuantity > item.product.stock.available
                            || item.orderedQuantity <= 0
                        )
                    })
                } else {
                    return true
                }
            },
            getTitle() {
                return this.editMode ? 'Edit' : this.viewMode ? 'View' : 'Create'
            }
        },
        watch: {
            '$route.params': function() {
                this.$emit('rerender')
            }
        },
        methods: {
            checkDisabledSendFromAvailable() {
                if (this.selectedProducts.length > 0) {
                    this.disabledSendFromAvailable = false
                    this.selectedProducts.forEach((item) => {
                        if (item.orderedQuantity > item.product.stock.available) {
                            this.disabledSendFromAvailable = true
                            return false
                        }
                    })
                } else {
                    this.disabledSendFromAvailable = true
                }
            },
            changeProductUnits(payload) {
                this.selectedProducts.map((item, index) => {
                    if (item.id === payload[index].id) {
                        item.orderedQuantity = payload[index].orderedQuantity
                    }
                    return item
                })
                this.disabledProductListButton = false
                this.checkDisabledSendFromAvailable()
            },
            editProduct(id) {
                this.productId = id
                this.$root.$emit('bv::show::modal', 'product-edit-modal')
            },
            editProductModal() {
                this.productWasEdit = true
                this.$root.$emit('bv::hide::modal', 'product-edit-modal')
            },
            closeModal() {
                this.productId = ''
                if (this.productWasEdit) {
                    this.$api('GET', this.$endpoints.orders.products(this.order.id)).then(response => {
                        this.selectedProducts = response
                    }).then(() => {
                        this.productWasEdit = false
                    })
                }
            },
            getOrder() {
                this.$api('GET', this.$endpoints.orders.single(this.order.id)).then(response => {
                    this.order = response
                    this.viewMode = this.order.fulfillment === true
                    this.editMode = this.order.status === 'new'
                    this.$api('GET', this.$endpoints.orders.products(this.order.id)).then(response => {
                        this.selectedProducts = response
                    })
                }).catch(() => {
                    this.$router.go(-1)
                }).finally(() => {
                    this.loadData = false
                })
            },
            rowClass(item) {
                if (!item) return
                if (item.product.status !== 'active' || item.orderedQuantity <= 0) {
                    return 'table-danger'
                }
            },
            getPopoverText(status) {
                switch (status) {
                    case 'suspended':
                        return 'This product has blank required fields. Please provide required information to proceed the work or remove this product from the list.'
                    case 'inactive':
                        return 'This product out of stock.'
                    default:
                        return 'This product have some problems. Please, connect to Administrator.'
                }
            },
            openPlanModal(product) {
                this.productId = product.product.id
                this.shippingPlanProduct = {
                    id: product.product.id,
                    quantity: product.orderedQuantity
                }
                this.$root.$emit('bv::show::modal', 'shipping-plan-modal')
            },
            addToOrder(product) {
                let productExistIndex = -1
                let productExist = this.selectedProducts.find((selectedProduct, index) => {
                    productExistIndex = index
                    return selectedProduct.product.id === product.id
                })

                if (!!productExist) {
                    console.log(this.selectedProducts[productExistIndex].orderedQuantity++)
                    this.$set(this.selectedProducts, productExistIndex, {
                        ...this.selectedProducts[productExistIndex],
                        orderedQuantity: this.selectedProducts[productExistIndex].orderedQuantity++
                    })
                } else {
                    this.selectedProducts.push({
                        product,
                        orderedQuantity: 1,
                        id: null
                    })
                }
            },
            removeFromOrder(productIdOfOrder, productId) {
                let selectedProductIndex = this.selectedProducts.findIndex(product => {
                    return productIdOfOrder ? product.id === productIdOfOrder : product.product.id === productId
                })
                if (selectedProductIndex !== -1) {

                    if (productIdOfOrder && !!this.selectedProducts[selectedProductIndex].id ) {
                        this.$api('DELETE', this.$endpoints.orders.product(this.order.id, productIdOfOrder))
                            .then(response => { this.selectedProducts.splice(selectedProductIndex, 1) })
                    } else {
                        this.selectedProducts.splice(selectedProductIndex, 1)
                    }
                }

            },
            startFulfillmentFromButton() {
                this.fulfillmentFromButton = true
                this.validateForm()
            },
            validateForm() {
                this.$refs['addressForm'].validateForm()
            },
            submittedForm(payload) {
                this.address = payload
                this.editMode ? this.saveChange() : this.order.id ? this.saveChange() : this.createOrder()
            },
            getOrderDelivery(order) {
                this.idForDelivery = order.id
                this.preferableDelivery = order.customerPreferableDelivery
                this.orderAddress = {
                    zip: order.address.postalCode,
                    address: order.address.line1,
                    cityName: order.address.city,
                    countryId: order.address.country.id
                }
                this.minInsurancePrice = order.owner ? + order.owner.minInsurancePrice : 0
                this.showLockers = order.owner ? order.owner.fulfillmentSettings.lockerEnabled : false
                this.$root.$emit('bv::show::modal', 'delivery-requests')
            },
            saveChange() {
                let body = {
                    address: this.address,
                    currencyCode: this.order.currencyCode,
                }
                let promises = [
                    this.saveSelectedProductOfOrder(),
                    this.$api('PUT', this.$endpoints.orders.single(this.order.id), body)
                ]
                Promise.all(promises)
                    .then(res => {
                        this.$store.dispatch('globalAlertAction', {
                            type: 'success',
                            title: 'Changes saved successfully',
                        })
                        if (this.fulfillmentFromButton) {
                            this.fulfillmentFromButton = false
                            this.getOrderDelivery(res[1])
                        } else {
                            this.$router.go(-1)
                        }
                    })
            },
            finishOrder() {
                this.$router.push({name: 'Orders'})
            },
            createOrder() {
                let body = {
                    address: this.address,
                    currencyCode: this.order.currencyCode,
                    products: []
                }
                this.disabledAddProductButton = true
                this.$api('POST', this.$endpoints.orders.list, body)
                    .then(response => {
                        this.order.id = response.id
                    })
                    .finally(() => this.disabledAddProductButton = false)
            },
            confirmOrderDemo() {
                this.$router.push({name: 'OrderFulfillment', params: {orderId: this.order.id}})
            },
            cancelOrder() {
                this.$store.dispatch('cancelOrder', this.order.id).then(() => this.$router.push({name: 'Orders'}))
            },
            deleteOrder() {
                this.$store.dispatch('deleteOrder', this.order.id).then(() => this.$router.push({name: 'Sales'}))
            },
            changeQuntityOfSelectedProduct({ productId, quantity }) {
                let selectedProductIndex = this.selectedProducts.findIndex(product => product.product.id === productId)
                if (selectedProductIndex > -1) {
                    this.$set(this.selectedProducts, selectedProductIndex, {
                        ...this.selectedProducts[selectedProductIndex],
                        orderedQuantity: quantity
                    })
                }
            },
            saveSelectedProductOfOrder() {
                let productsListData = this.selectedProducts.map(selectedProduct => {
                    return {
                        productId: selectedProduct.product.id,
                        quantity: +selectedProduct.orderedQuantity
                    }
                })
                return this.$api('PATCH', this.$endpoints.orders.addProductsToOrder(this.order.id), {products: productsListData})
                    .then((response) => {
                        this.selectedProducts = response.orderedProducts
                    })
            }
        }
    }
</script>



// WEBPACK FOOTER //
// src/components/UserPanel/Pages/Orders/SingleOrder/SingleOrder.vue